import { Button } from 'components/universal/button';
import { Controller } from 'react-hook-form';
import Select, { components } from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import { Workplace } from 'views/employee-management/useEmployees';
import { ReactComponent as ArrowDown } from '../../../assets/images/icon/icon-down.svg';
import { Input } from '../input';
import cls from './addMutipleNewUser.module.scss';
import { useAddMultipleNewUserForm } from './useAddMultipleNewUserForm';

export const DropdownIndicator = (props) => {
    return (
        <>
            <components.DropdownIndicator {...props}>
                <ArrowDown />
            </components.DropdownIndicator>
        </>
    );
};

export interface AddMultipleNewUserValue {
    count: number | null;
    workplace_id: string;
    department_id: string | number;
}

const defaultValues = {
    count: null,
    workplace_id: '',
    department_id: '',
};

export type AddMultipleNewUserRequest = {
    data: AddMultipleNewUserValue;
};

interface AddNewUserFormInterface {
    adminWorkplaces: Workplace[];
    selectedWorkplaceId?: number;
    onCancel: (boolean) => void;
}

export const AddMultipleNewUserForm = ({
    adminWorkplaces,
    selectedWorkplaceId,
    onCancel
}: AddNewUserFormInterface) => {
    const {
        t,
        form,
        workplaceId,
        selectedDepartment,
        loadDepartmentOptions,
        handleDepartmentChange,
        onSubmit,
        isSubmitting,
    } = useAddMultipleNewUserForm({ adminWorkplaces, onCancel });

    const { register, handleSubmit, watch, control, formState: { errors } } = form;

    return (
        <>
            <h3 className={cls.modalHeader}>
                {t('employeeManagement.addMultipleNewUser')}
            </h3>
            <p className={cls.modalDescription}>
                {t('employeeManagement.addMultipleNewUserDescription')}
            </p>
            <form
                className={cls.form}
                onSubmit={handleSubmit(onSubmit)}
                noValidate
            >
                <Input
                    className={cls.inputFull}
                    register={register}
                    id="count"
                    name="count"
                    label={t('employeeManagement.count')}
                    error={t(errors.count?.message)}
                    maxLength={255}
                />
                <div className={cls.selectCustomWrapper}>
                    <p>{t('employeeManagement.workplace_id')}</p>
                    <Controller
                        name="workplace_id"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                className="react-select-container"
                                options={adminWorkplaces}
                                getOptionLabel={(option: Workplace) => option.workplace_name}
                                getOptionValue={(option: Workplace) => option.id.toString()}
                                placeholder={t('employeeManagement.workplace_id')}
                                onChange={(option: Workplace | null) => field.onChange(option?.id ?? '')}
                                value={adminWorkplaces.find(w => w.id === Number(field.value)) || null}
                                components={{ DropdownIndicator }}
                                classNamePrefix="react-select"
                            />
                        )}
                    />
                </div>
                <div className={`${cls.selectCustomWrapper} ${workplaceId === 0 ? cls.disabled : ''}`}>
                    <label htmlFor="department_id" className={cls.label}>
                        {t('registration.workplaceDepartments')}
                    </label>
                    <Controller
                        name="department_id"
                        control={control}
                        render={({ field }) => (
                            <AsyncPaginate
                                key={workplaceId}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                components={{ DropdownIndicator }}
                                placeholder=""
                                isDisabled={workplaceId === 0}
                                value={selectedDepartment}
                                loadOptions={loadDepartmentOptions}
                                additional={{ page: 1 }}
                                onChange={(selected) => handleDepartmentChange(selected as any, field.onChange)}
                                noOptionsMessage={() => t('registration.noDepartments')}
                                debounceTimeout={300}
                            />
                        )}
                    />
                    {errors.department_id && (
                        <p className={cls.errorBox}>{t(errors.department_id.message)}</p>
                    )}
                </div>
                <div className={cls.modalButtonWrapper}>
                    <Button
                        className={cls.modalButton}
                        styleType="border"
                        type="button"
                        onClick={onCancel}
                    >
                        {t('employeeManagement.cancel')}
                    </Button>
                    <Button className={cls.modalButton} type="submit">
                        {t('employeeManagement.sendAndGenerate')}
                    </Button>
                </div>
            </form>
        </>
    );
};